import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { 
  Users, 
  Plus, 
  Edit3, 
  Trash2, 
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Building,
  DollarSign,
  User
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const BusinessUnitManager = ({ calculationId, onUnitsUpdate }) => {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state for adding/editing business units
  const [showForm, setShowForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [formData, setFormData] = useState({
    unit_name: "",
    unit_code: "",
    description: "",
    headcount: "",
    annual_payroll: "",
    annual_revenue: ""
  });

  useEffect(() => {
    loadBusinessUnits();
  }, []);

  const loadBusinessUnits = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/business-units`);
      const units = response.data.business_units || [];
      setBusinessUnits(units);
      
      if (onUnitsUpdate) {
        onUnitsUpdate(units);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error loading business units");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBusinessUnit = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    // Validation
    if (!formData.unit_name.trim() || !formData.unit_code.trim()) {
      setError("Unit name and code are required");
      setSaving(false);
      return;
    }

    // Check for duplicate unit codes
    const duplicateCode = businessUnits.find(unit => 
      unit.unit_code === formData.unit_code && unit.id !== editingUnit?.id
    );
    if (duplicateCode) {
      setError("Unit code must be unique");
      setSaving(false);
      return;
    }

    try {
      const unitData = {
        unit_name: formData.unit_name.trim(),
        unit_code: formData.unit_code.trim().toUpperCase(),
        description: formData.description.trim() || null,
        headcount: formData.headcount ? parseInt(formData.headcount) : null,
        annual_payroll: formData.annual_payroll ? parseFloat(formData.annual_payroll) : null,
        annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
        created_by: "user"
      };

      const response = await axios.post(`${API}/business-units`, unitData);

      if (response.data.success) {
        setSuccess(editingUnit ? "Business unit updated successfully!" : "Business unit created successfully!");
        resetForm();
        loadBusinessUnits(); // Reload the list
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error saving business unit");
    } finally {
      setSaving(false);
    }
  };

  const handleEditUnit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      unit_name: unit.unit_name || "",
      unit_code: unit.unit_code || "",
      description: unit.description || "",
      headcount: unit.headcount ? unit.headcount.toString() : "",
      annual_payroll: unit.annual_payroll ? unit.annual_payroll.toString() : "",
      annual_revenue: unit.annual_revenue ? unit.annual_revenue.toString() : ""
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      unit_name: "",
      unit_code: "",
      description: "",
      headcount: "",
      annual_payroll: "",
      annual_revenue: ""
    });
    setEditingUnit(null);
    setShowForm(false);
  };

  const formatCurrency = (value) => {
    return value ? `$${value.toLocaleString()}` : 'N/A';
  };

  const calculateTotals = () => {
    return {
      totalHeadcount: businessUnits.reduce((sum, unit) => sum + (unit.headcount || 0), 0),
      totalPayroll: businessUnits.reduce((sum, unit) => sum + (unit.annual_payroll || 0), 0),
      totalRevenue: businessUnits.reduce((sum, unit) => sum + (unit.annual_revenue || 0), 0)
    };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading business units...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-red-700">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-green-700">
            {success}
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Statistics */}
      {businessUnits.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Building className="h-5 w-5 text-blue-600" />
              <span>Business Unit Summary</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-blue-900">Total Business Units</div>
                <div className="text-2xl font-bold text-blue-600">{businessUnits.length}</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-green-900">Total Headcount</div>
                <div className="text-2xl font-bold text-green-600">{totals.totalHeadcount}</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-blue-900">Total Payroll</div>
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(totals.totalPayroll)}
                </div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-orange-900">Total Revenue</div>
                <div className="text-2xl font-bold text-orange-600">
                  {formatCurrency(totals.totalRevenue)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Business Units Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-indigo-600" />
              <span>Business Units</span>
            </CardTitle>
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Business Unit
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {businessUnits.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium mb-2 text-slate-600">No Business Units</h3>
              <p className="text-sm text-slate-500 mb-4">
                Create business units to enable three-factor allocation and segmented cost distribution.
              </p>
              <Button 
                onClick={() => setShowForm(true)}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Business Unit
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unit Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Headcount</TableHead>
                    <TableHead className="text-right">Annual Payroll</TableHead>
                    <TableHead className="text-right">Annual Revenue</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {businessUnits.map((unit) => (
                    <TableRow key={unit.id}>
                      <TableCell className="font-medium">{unit.unit_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{unit.unit_code}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-600 max-w-48 truncate">
                        {unit.description || "No description"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <User className="h-3 w-3 text-slate-400" />
                          <span>{unit.headcount || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <DollarSign className="h-3 w-3 text-slate-400" />
                          <span>{formatCurrency(unit.annual_payroll)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <DollarSign className="h-3 w-3 text-slate-400" />
                          <span>{formatCurrency(unit.annual_revenue)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditUnit(unit)}
                        >
                          <Edit3 className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
              <Users className="h-5 w-5" />
              <span>{editingUnit ? "Edit Business Unit" : "Add Business Unit"}</span>
            </h3>
            
            <div className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="unit_name">Unit Name *</Label>
                  <Input
                    id="unit_name"
                    placeholder="e.g., Engineering Division"
                    value={formData.unit_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="unit_code">Unit Code *</Label>
                  <Input
                    id="unit_code"
                    placeholder="e.g., ENG"
                    value={formData.unit_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, unit_code: e.target.value.toUpperCase() }))}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe this business unit's function and scope..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>

              {/* Three-Factor Data */}
              <div>
                <h4 className="font-semibold mb-3 text-slate-700">Three-Factor Allocation Data</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="headcount">Headcount</Label>
                    <Input
                      id="headcount"
                      type="number"
                      min="0"
                      placeholder="25"
                      value={formData.headcount}
                      onChange={(e) => setFormData(prev => ({ ...prev, headcount: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="annual_payroll">Annual Payroll ($)</Label>
                    <Input
                      id="annual_payroll"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="2500000.00"
                      value={formData.annual_payroll}
                      onChange={(e) => setFormData(prev => ({ ...prev, annual_payroll: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="annual_revenue">Annual Revenue ($)</Label>
                    <Input
                      id="annual_revenue"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="8000000.00"
                      value={formData.annual_revenue}
                      onChange={(e) => setFormData(prev => ({ ...prev, annual_revenue: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Three-factor data is used for residual cost allocation. Leave blank if not applicable.
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  resetForm();
                  setError(null);
                  setSuccess(null);
                }}
                disabled={saving}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleSaveBusinessUnit}
                disabled={saving || !formData.unit_name.trim() || !formData.unit_code.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : (editingUnit ? "Update Unit" : "Create Unit")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Information Panel */}
      <div className="p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center space-x-2 text-sm text-slate-600">
          <AlertCircle className="h-4 w-4" />
          <div>
            <p className="font-medium">Business Unit Guidelines</p>
            <ul className="text-xs mt-1 space-y-1">
              <li>• Unit codes must be unique and will be used for allocation tracking</li>
              <li>• Three-factor data (headcount, payroll, revenue) enables residual cost allocation</li>
              <li>• Business units can represent divisions, departments, or any organizational segments</li>
              <li>• At least one business unit with three-factor data is required for advanced allocations</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessUnitManager;